/**
 * Preferência de tema — claro ou escuro.
 *
 * O padrão é o escuro, e não a preferência do sistema. A MPC é uma marca preta
 * com verde-limão: o escuro é a peça original, e o claro existe para quem precisa
 * dele — sala clara, tela no sol, olho que cansa com texto branco no fundo
 * preto. Seguir `prefers-color-scheme` viraria a interface de todo capitão que
 * já usa o produto sem ninguém ter pedido, e ainda faria a primeira impressão
 * da marca depender de uma configuração de sistema operacional. Quem quer o
 * claro escolhe uma vez, e a escolha fica.
 *
 * Vale para a página inteira: o atributo mora no `<html>`, e o `theme.css`
 * redefine os tokens a partir dele. Nenhum componente decide cor sozinho.
 */

export type Tema = 'claro' | 'escuro';

/** Namespace no `localStorage` para o dia em que houver uma segunda preferência. */
const CHAVE = 'mpc:tema';

/**
 * Cor da barra do navegador no celular. Sem atualizar isto, o topo do Chrome no
 * Android continua preto com a página branca embaixo — e o aplicativo instalado
 * fica com uma faixa preta permanente que ninguém consegue explicar.
 */
const COR_DA_BARRA: Record<Tema, string> = {
  escuro: '#171717',
  claro: '#F5F5F5',
};

/** Ouvintes do React. `useSyncExternalStore` precisa de um ponto de inscrição. */
const ouvintes = new Set<() => void>();

export function lerTema(): Tema {
  if (typeof document === 'undefined') return 'escuro';
  return document.documentElement.dataset.theme === 'light' ? 'claro' : 'escuro';
}

export function aplicarTema(tema: Tema): void {
  document.documentElement.dataset.theme = tema === 'claro' ? 'light' : 'dark';

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', COR_DA_BARRA[tema]);

  /*
   * Gravar pode falhar — janela anônima, cookies de terceiros bloqueados,
   * armazenamento cheio. Quando falha, o tema vale para esta visita e volta ao
   * escuro na próxima. É degradação aceitável; derrubar a troca de tema por
   * causa disso não é.
   */
  try {
    localStorage.setItem(CHAVE, tema);
  } catch {
    /* preferência não persiste nesta sessão */
  }

  for (const ouvinte of ouvintes) ouvinte();
}

export function alternarTema(): void {
  aplicarTema(lerTema() === 'claro' ? 'escuro' : 'claro');
}

/**
 * Inscrição para o React.
 *
 * O `storage` entra junto para que trocar o tema numa aba acerte as outras —
 * duas abas da mesma plataforma com temas diferentes é o tipo de coisa que o
 * usuário lê como defeito, não como recurso.
 */
export function assinarTema(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);

  const emOutraAba = (e: StorageEvent) => {
    if (e.key !== CHAVE || !e.newValue) return;
    const tema: Tema = e.newValue === 'claro' ? 'claro' : 'escuro';
    document.documentElement.dataset.theme = tema === 'claro' ? 'light' : 'dark';
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', COR_DA_BARRA[tema]);
    ouvinte();
  };
  window.addEventListener('storage', emOutraAba);

  return () => {
    ouvintes.delete(ouvinte);
    window.removeEventListener('storage', emOutraAba);
  };
}
